import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { InternetConnectionService } from '../core/internet-connection.service';
import { NetworkPermissionService } from '../core/network-permission.service';
import { AuthTokenStorageService } from '../init/auth-token-storage.service';
import { ToastService } from '../shared/toast/toast.service';
import { AuthAlertService } from './auth-alert.service';
import { AuthService } from './auth.service';

/** Pass untouched request through to the next request handler. */
@Injectable()
export class GlobalInterceptor implements HttpInterceptor {
  private readonly skipAuthHeaderPaths = [
    '/autenticacao/token',
    '/autenticacao/token/atualizacao',
    '/auth/login',
    '/auth/refresh',
  ];

  constructor(
    private auth: AuthService,
    private toastService: ToastService,
    private alert: AuthAlertService,
    private authTokenStorage: AuthTokenStorageService,
    private networkPermission: NetworkPermissionService,
    private internetConnection: InternetConnectionService
  ) { }

  applyAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
    let dupReq: HttpRequest<any>;
    let token: string = this.authTokenStorage.getAccessToken();

    if (token !== '' && !this.shouldSkipAuthHeader(req))
      dupReq = req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
    else
      dupReq = req;

    return dupReq;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let dupReq: HttpRequest<any>;

    if (req.headers.get("Content-Type") === null && this.shouldAttachJsonContentType(req))
      dupReq = req.clone({ headers: req.headers.set('Content-Type', `application/json`) });
    else if (req.headers.get("Content-Type") === 'null')
      dupReq = req.clone({ headers: req.headers.delete('Content-Type') });
    else
      dupReq = req.clone();

    return next.handle(this.applyAuthHeader(dupReq)).pipe(
      catchError((e: HttpErrorResponse) => this.handleHttpError(e, dupReq))
    );
  }

  private shouldSkipAuthHeader(req: HttpRequest<any>): boolean {
    const url = req.url.toLowerCase();
    return this.skipAuthHeaderPaths.some(path => url.includes(path));
  }

  private shouldAttachJsonContentType(req: HttpRequest<any>): boolean {
    const method = req.method.toUpperCase();
    const supportsBody = method === 'POST' || method === 'PUT' || method === 'PATCH';
    return supportsBody && req.body !== null && req.body !== undefined;
  }

  private handleHttpError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    console.log(`Interceptor - Error ${error.status}`, error);
    (error as any)._handled = true;

    // Se o erro for um Blob, precisamos processá-lo de forma assíncrona
    if (error.error instanceof Blob) {
      return from(this.parseBlobError(error)).pipe(
        switchMap((parsedError: HttpErrorResponse) => {
          return this.routeError(parsedError, request);
        })
      );
    }

    return this.routeError(error, request);
  }

  private routeError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    // Erros com mensagem customizada do servidor (>=400 && !401 && !403)
    if (this.isServerErrorWithMessage(error)) {
      return this.handleServerErrorWithMessage(error);
    }

    // Erros de autenticação/autorização (401, 403)
    if (this.isAuthError(error)) {
      return this.handleAuthError(error);
    }

    // Erros de rede/conexão (0, 504)
    if (this.isNetworkError(error)) {
      return this.handleNetworkError(error, request);
    }

    // Outros erros do servidor (>= 400) sem mensagem customizada
    if (this.isServerErrorWithoutMessage(error)) {
      return this.handleServerErrorWithoutMessage(error, request);
    }

    // Qualquer outro erro
    return this.handleUnknownError(error);
  }

  private async parseBlobError(error: HttpErrorResponse): Promise<HttpErrorResponse> {
    try {
      const blob = error.error as Blob;
      const text = await blob.text();
      const json = JSON.parse(text);

      // Modifica o error.error do objeto original ao invés de criar um novo
      // Isso preserva o stack trace e todas as propriedades originais
      (error as any).error = json;

      return error;
    } catch (e) {
      // Se falhar ao parsear, retorna o erro original
      console.error('Failed to parse blob error:', e);
      return error;
    }
  }

  private isServerErrorWithMessage(error: HttpErrorResponse): boolean {
    let errorObj = error?.error;

    return !!(errorObj?.mensagem || errorObj?.mensagens || errorObj?.message) && (this.isAuthError(error) === false);
  }

  private isAuthError(error: HttpErrorResponse): boolean {
    return error.status === 403 || error.status === 401;
  }

  private isNetworkError(error: HttpErrorResponse): boolean {
    return error.status === 0 || !error.status || error.status === 504;
  }

  private isServerErrorWithoutMessage(error: HttpErrorResponse): boolean {
    return error.status >= 400;
  }

  private handleServerErrorWithMessage(error: HttpErrorResponse): Observable<never> {
    let errorObj = error?.error;

    let mensagem = errorObj.mensagem || errorObj.message || (errorObj.mensagens && errorObj.mensagens.join('<br/>'));

    this.toastService.showWarning(mensagem);
    return throwError(() => error);
  }

  private handleAuthError(error: HttpErrorResponse): Observable<never> {
    this.alert.showNoPermissionAction();
    return throwError(() => error);
  }

  private handleNetworkError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    return from(this.networkPermission.isLocalNetworkAccessError(request.url, error.status)).pipe(
      switchMap((isLNAError: boolean) => {
        if (isLNAError) {
          return this.handleLocalNetworkAccessError(error, request);
        }
        return this.handleConnectionError(error, request);
      })
    );
  }

  private handleLocalNetworkAccessError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    console.error('Local Network Access permission denied');

    if (this.isSMOLinkRequest(request)) {
      // Quando o SMOLink não está em execução o navegador não apresenta o prompt de permissão.
      this.toastService.showError(
        'O SMOLink não está respondendo. Verifique se o aplicativo SMOLink está em execução ao lado do relógio do sistema ' +
        'e se foi permitido acesso à rede local nas configurações do navegador.'
      );
      return throwError(() => error);
    }

    if (this.isSMOAssinadorRequest(request)) {
      this.toastService.showError(
        'O SMOAssinador não está respondendo. Verifique se o aplicativo SMOAssinador está em execução ao lado do relógio do sistema ' +
        'e se foi permitido acesso à rede local nas configurações do navegador.'
      );
      return throwError(() => error);
    }

    this.toastService.showError(
      'Acesso à rede local negado. Por favor, permita o acesso à rede local nas configurações do navegador.'
    );

    return throwError(() => error);
  }

  private handleConnectionError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    // Verifica se é uma requisição para o SMOLink local
    if (this.isSMOLinkRequest(request)) {
      return this.handleSMOLinkError(error);
    }

    if (this.isSMOAssinadorRequest(request)) {
      return this.handleSMOAssinadorError(error);
    }

    return this.internetConnection.checkInternetConnection().pipe(
      switchMap((hasInternet: boolean) => {
        if (!hasInternet) {
          return this.handleNoInternetError(error);
        }
        return this.handleServerUnavailableError(error);
      })
    );
  }

  private isSMOLinkRequest(request: HttpRequest<any>): boolean {
    const url = request.url.toLowerCase();
    const isLocalhost = url.includes('localhost') || url.includes('127.0.0.1') || url.includes('::1');
    const hasSMOLink = url.includes('smolink') || url.includes(':45675');
    return isLocalhost && hasSMOLink;
  }

  private isSMOAssinadorRequest(request: HttpRequest<any>): boolean {
    const url = request.url.toLowerCase();
    const isLocalhost = url.includes('localhost') || url.includes('127.0.0.1') || url.includes('::1');
    const hasSMOAssinador = url.includes('smoassinador') || url.includes(':45684');
    return isLocalhost && hasSMOAssinador;
  }

  private handleSMOLinkError(error: HttpErrorResponse): Observable<never> {
    console.error('SMOLink application not responding');
    this.toastService.showError(
      'O SMOLink não está respondendo. Verifique se o aplicativo SMOLink está em execução ao lado do relógio do sistema.'
    );
    return throwError(() => error);
  }

  private handleSMOAssinadorError(error: HttpErrorResponse): Observable<never> {
    console.error('SMOAssinador application not responding');
    this.toastService.showError(
      'O SMOAssinador não está respondendo. Verifique se o aplicativo SMOAssinador está em execução ao lado do relógio do sistema.'
    );
    return throwError(() => error);
  }

  private handleNoInternetError(error: HttpErrorResponse): Observable<never> {
    console.error('No internet connection detected');
    this.toastService.showError(
      'Sem conexão com a internet. Verifique sua conexão e tente novamente.'
    );

    return throwError(() => error);
  }

  private handleServerUnavailableError(error: HttpErrorResponse): Observable<never> {
    this.toastService.showError(
      'Houve uma instabilidade ao processar a solicitação. Tenta novamente em alguns instantes. Se continuar ocorrendo, entra em contato com o suporte.'
    );
    //***Sentry.captureException(error);
    return throwError(() => error);
  }

  /**
   * Erros do servidor sem mensagem customizada (>=400). Retorna mensagem genérica.
   * @param error
   * @returns 
   */
  private handleServerErrorWithoutMessage(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    if (error.status === 400 && this.isSMOLinkRequest(request)) {
      // SMOLink retorna 400 sem mensagem em alguns casos.
      return EMPTY;
    }

    this.toastService.showError(
      'Ocorreu um erro inesperado no servidor (código: 500X). Tenta novamente em alguns instantes. Se persistir, entra em contato com o suporte.'
    );
    //***Sentry.captureException(error);
    return throwError(() => error);
  }

  /**
   * Erros desconhecidos (não tratados). Retorna mensagem genérica.
   * @param error
   * @returns
   */
  private handleUnknownError(error: HttpErrorResponse): Observable<never> {
    this.toastService.showError(
      'Ocorreu um erro inesperado (código: 500UK). Se o problema persistir, por favor, contate o suporte.'
    );
    //***Sentry.captureException(error);
    return throwError(() => error);
  }
}
